import type { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase/client'

/**
 * Whether the person got their text, as one line from their latest one:
 * on its way, delivered, not delivered (with the network's reason), never
 * sent (with why), or not confirmed (no receipt within an hour).
 */
export type SmsDeliveryState = 'sending' | 'delivered' | 'not_delivered' | 'not_sent' | 'unconfirmed'

export type SmsDeliveryStatus = Readonly<{
  state: SmsDeliveryState
  /** The network's or the server's reason, for not delivered and not sent. */
  reason: string | null
  /** When it was delivered, queued, or last changed. */
  at: string | null
  /** Whether sending again could help. */
  canResend: boolean
  /** Set while it waits in the queue, for Send now. */
  queuedSince: string | null
}>

export type SmsQueuedAttempt = Readonly<{
  status: 'queued'
  attemptCount: number
}>

export type SmsServiceErrorCode = 'offline' | 'not-eligible' | 'not-found' | 'not-allowed' | 'already-waiting' | 'too-soon' | 'daily-limit' | 'already-delivered' | 'cannot-text' | 'unexpected'

export class SmsServiceError extends Error {
  constructor(public readonly code: SmsServiceErrorCode, message: string) {
    super(message)
    this.name = 'SmsServiceError'
  }
}

type SmsStatusRow = {
  registration_id: string
  state: string
  reason: string | null
  state_at: string | null
  can_resend: boolean
  queued_since: string | null
}

type SmsQueuedRow = {
  status: string
  attempt_count: number
  attempted_at: string
}

function requireOnline(message: string) {
  if (!navigator.onLine) throw new SmsServiceError('offline', message)
}

const states: readonly SmsDeliveryState[] = ['sending', 'delivered', 'not_delivered', 'not_sent', 'unconfirmed']

function mapStatus(row: SmsStatusRow): SmsDeliveryStatus {
  // Anything unexpected reads as not confirmed: that never claims a text
  // arrived when we don't know it did.
  const state = states.includes(row.state as SmsDeliveryState) ? row.state as SmsDeliveryState : 'unconfirmed'
  return { state, reason: row.reason, at: row.state_at, canResend: row.can_resend === true, queuedSince: row.queued_since }
}

/**
 * The reason in words a collector can act on, from ClickSend's receipt text
 * (https://help.clicksend.com/en/articles/42318-sms-error-codes) or the
 * server's own. Null when there is nothing useful to add.
 */
export function deliveryReason(reason: string | null): string | null {
  if (!reason) return null
  const text = reason.toLowerCase()
  const known: ReadonlyArray<readonly [string, string]> = [
    ['no country code', 'the number has no country code'],
    ['cannot be sent a text message', 'that isn’t a phone number'],
    ['unknown subscriber', 'the number isn’t in use'],
    ['unidentified subscriber', 'the network didn’t recognise the number'],
    ['not supporting sms', 'this number can’t receive texts'],
    ['absent subscriber', 'their phone has been off or out of signal'],
    ['memory capacity', 'their phone’s memory is full'],
    ['anti-spam', 'their network’s spam filter blocked it'],
    ['illegal subscriber', 'their network blocked it'],
    ['receipt expired', 'the network gave up trying'],
    ['timeout', 'the network timed out'],
    ['routing', 'the network couldn’t route it'],
    ['rejected', 'their network rejected it'],
    ['provider rejected the message', 'ClickSend refused it'],
  ]
  return known.find(([match]) => text.includes(match))?.[1] ?? null
}

export interface SmsService {
  /**
   * The latest delivery status for each registration that has been messaged.
   * Registrations with no attempt are simply absent: an attendee who was never
   * sent a message is not a failure to report.
   */
  readStatuses(registrationIds: string[]): Promise<Readonly<Record<string, SmsDeliveryStatus>>>
  /** Queues a further attempt against the registration's existing ticket. */
  resend(registrationId: string): Promise<SmsQueuedAttempt>
  /**
   * Asks the delivery function to drain pending attempts. Deliberately
   * fire-and-forget for its callers: a message that cannot be sent right now
   * must never hold up the registration that caused it.
   */
  requestDrain(): Promise<void>
}

export function createSmsService(client: SupabaseClient = supabase): SmsService {
  return {
    async readStatuses(registrationIds) {
      // Nothing to ask about, so do not spend a round trip on it.
      if (registrationIds.length === 0) return {}
      requireOnline('Reconnect to see these message statuses.')
      const result = await client.rpc('sms_delivery_statuses', { p_registration_ids: registrationIds }) as unknown as { data: SmsStatusRow[] | null; error: { message?: string } | null }
      if (result.error) throw new SmsServiceError('unexpected', 'Message statuses could not be loaded. Please try again.')
      const statuses: Record<string, SmsDeliveryStatus> = {}
      for (const row of result.data ?? []) statuses[row.registration_id] = mapStatus(row)
      return statuses
    },
    async resend(registrationId) {
      requireOnline('Reconnect to resend this message.')
      const result = await client.rpc('queue_ticket_sms', { p_registration_id: registrationId }) as unknown as { data: SmsQueuedRow[] | null; error: { code?: string; message?: string } | null }
      if (result.error?.code === '22023') throw new SmsServiceError('not-eligible', 'This attendee cannot receive a text message.')
      if (result.error?.code === '42501') throw new SmsServiceError('not-allowed', 'Only the person who registered them, whoever opened their session, or someone with Events can resend.')
      if (result.error?.code === '55000') throw new SmsServiceError('already-waiting', 'A message is already on its way.')
      if (result.error?.code === 'P0429') throw new SmsServiceError('too-soon', 'Wait a couple of minutes before sending another message.')
      if (result.error?.code === 'P0430') throw new SmsServiceError('daily-limit', 'This person has had 3 resends today. Try again tomorrow.')
      if (result.error?.code === 'P0431') throw new SmsServiceError('already-delivered', 'Their text was delivered. If they’ve lost it, use View ticket, then Share ticket.')
      if (result.error?.code === 'P0432') throw new SmsServiceError('cannot-text', 'This number can’t be texted. Use View ticket, then Share ticket.')
      if (result.error?.code === 'P0002') throw new SmsServiceError('not-found', 'This message has not been issued a ticket.')
      if (result.error) throw new SmsServiceError('unexpected', 'The message could not be queued. Please try again.')
      const row = result.data?.[0]
      if (!row) throw new SmsServiceError('unexpected', 'The message could not be queued. Please try again.')
      return { status: 'queued', attemptCount: row.attempt_count }
    },
    async requestDrain() {
      const result = await client.functions.invoke('send-ticket-sms', { method: 'POST' }) as unknown as { error: { message?: string } | null }
      if (result.error) throw new SmsServiceError('unexpected', 'Pending messages could not be sent right now.')
    },
  }
}

export const smsService = createSmsService()
